/**
 * @file src/api/textureCatalog.js
 * @description Where the platform's texture catalogue comes from, for this app.
 *
 * SOURCE OF TRUTH: the catalogue the backend serves. It declares the skyboxes, their ids and which
 * one is default, and it is what a texture chosen on the platform actually changes.
 *
 * The shared package holds the loader and a hard-coded fallback, but it has no opinion about WHERE
 * the catalogue lives — the app has to say. Without that it silently answers from the fallback, so
 * the catalogue could name a different default and this app would never see it: a second source of
 * truth wearing the first one's name, which is the whole reason the skybox was worth moving here.
 *
 * The route is spelled `entangled-worlds/...` for historical reasons. It is a platform endpoint that
 * both apps read, not a reach into the other app — nothing is imported across that boundary.
 */
import { loadTextureConfig } from 'entangled-worlds-orbiters-shared/config';
import { fetchJsonFromApi, resolveApiBase } from './httpClient.js';

/**
 * The catalogue.
 *
 * Resolves to the shared package's hard-coded fallback rather than rejecting when the API is
 * unreachable or unconfigured: a world with a default sky is better than no world, and this is a
 * backdrop.
 *
 * The shared loader's own cache is deliberately NOT read. It caches whatever it last returned —
 * including that fallback — and offers no way to invalidate it, so a single cold-boot failure (a
 * 5xx, or an API base that is not on `window` yet) would answer every later question in the session
 * from the fallback, permanently and silently. That is the second source of truth this module exists
 * to prevent, so a failure is allowed to cost one refetch instead of the whole session. The answer
 * is asked for once per sky, which is already six texture faces, so the extra request is noise.
 *
 * @returns {Promise<object>} the texture configuration
 */
export function loadPlatformTextureCatalog() {
    return loadTextureConfig(platformTextureCatalogUrl(), false);
}

/**
 * The catalogue, or null — with NO fallback.
 *
 * The difference from {@link loadPlatformTextureCatalog} is the whole reason this exists. That one
 * answers a failed fetch with a copy hard-coded in the shared package, silently and on purpose: a
 * world with a default sky beats no world. A DOWNLOAD cannot take that trade. The hard-coded copy is
 * free to name a different default than the platform serves, so storing the sky it names would pin
 * six faces the renderer never asks for and then call the sky downloaded. Anything that has to know
 * whether it got the real document asks here, and stores nothing when the answer is null.
 *
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<object|null>}
 */
export async function readPlatformTextureCatalog({ signal } = {}) {
    try {
        const response = await fetchJsonFromApi('/entangled-worlds/preview-config-files', { signal });
        if (!response?.ok) return null;
        const catalogue = await response.json();
        // The same shape check the shared loader makes before it trusts a document. A 200 that is not
        // a catalogue — an error envelope, a sign-in page — must not be stored as one.
        return catalogue?.textures && typeof catalogue.textures === 'object' ? catalogue : null;
    } catch (error) {
        if (error?.name === 'AbortError') throw error;
        return null;
    }
}

/**
 * Where the catalogue lives, as a URL.
 *
 * Exported because a download has to STORE it: the catalogue is the document that says which sky is
 * default and where the cloud and moon files live, and a download that keeps every texture and not
 * this one keeps files nothing will ask for. Named here, once, so the URL a download pins is the URL
 * the loader asks for — two spellings of it would mean the pinned copy is never found.
 *
 * @returns {string|null} null when no API base is configured
 */
export function platformTextureCatalogUrl() {
    const base = resolveApiBase();
    return base ? `${base}/entangled-worlds/preview-config-files` : null;
}
